import { useState, useEffect } from "react";
import { X, Layers } from "lucide-react";
import { ResponsiveSheet } from "./ResponsiveSheet";
import type { Thought } from "./ThoughtCard";
import type { Thread } from "./BurrowStore";
import { supabase } from "../lib/supabaseClient";
import { useBurrow } from "./BurrowStore";
import { useNavigate } from "react-router";

interface ThreadSidebarProps {
  threadId: string | null;
  title?: string;
  thoughtIds?: string[];
  onClose: () => void;
  onSelectThought?: (id: string) => void;
}

export function ThreadSidebar({ threadId, title, thoughtIds, onClose, onSelectThought }: ThreadSidebarProps) {
  const { state, setActiveThread, ensureThoughtsLoaded } = useBurrow();
  const navigate = useNavigate();
  const [thoughts, setThoughts] = useState<Thought[]>([]);

  const thread = threadId ? state.savedThreads.find((t) => t.id === threadId) : null;
  const displayTitle = title || thread?.title || "Trail";
  const isOpen = !!threadId || (thoughtIds && thoughtIds.length > 0);

  useEffect(() => {
    // If thought IDs are provided directly (e.g. active thread), use those
    if (thoughtIds && thoughtIds.length > 0) {
      // A trail can reach back further than the loaded pages; pull in whatever
      // is missing so stops don't silently drop out of the sequence.
      void ensureThoughtsLoaded(thoughtIds);
      const cards = thoughtIds.map((id) => state.thoughts.find((t) => t.id === id)).filter(Boolean) as Thought[];
      setThoughts(cards);
      return;
    }
    if (!threadId) { setThoughts([]); return; }
    (async () => {
      const { data } = await supabase
        .from("thread_thoughts")
        .select("thought_id,position")
        .eq("thread_id", threadId)
        .order("position", { ascending: true });
      if (data) {
        const ids = data.map((r) => r.thought_id);
        void ensureThoughtsLoaded(ids);
        const cards = ids.map((id) => state.thoughts.find((t) => t.id === id)).filter(Boolean) as Thought[];
        setThoughts(cards);
      }
    })();
  }, [threadId, thoughtIds, state.thoughts, ensureThoughtsLoaded]);

  return (
    <ResponsiveSheet
      open={!!isOpen}
      onClose={onClose}
      label="Trail"
      desktop="right"
      desktopWidth="max-w-md"
      mobileHeight="tall"
      zIndex={40}
    >
            <div className="p-5 md:p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Layers size={16} className="text-accent" />
                  <h2
                    className="text-primary"
                    style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "1.1rem", fontWeight: 400 }}
                  >
                    {displayTitle}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
                >
                  <X size={16} />
                </button>
              </div>

              {thoughts.length > 0 ? (
                <div className="space-y-4">
                  {thoughts.map((thought, i) => (
                    <div key={thought.id} className="relative pl-8">
                      {/* Step dot */}
                      <div className="absolute left-0 top-2">
                        <div className={`w-2.5 h-2.5 rounded-full border-2 ${
                          i === thoughts.length - 1
                            ? "bg-accent border-accent"
                            : "bg-card border-accent/40"
                        }`} />
                      </div>
                      {/* Vertical line */}
                      {i < thoughts.length - 1 && (
                        <div className="absolute left-[4px] top-5 bottom-[-12px] w-px bg-accent/20" />
                      )}

                      <div
                        className="rounded-xl bg-background p-4 cursor-pointer hover:bg-accent/5 transition-all"
                        onClick={() => {
                          onClose();
                          onSelectThought?.(thought.id);
                        }}
                      >
                        <span className="text-accent/40 block mb-1" style={{ fontSize: "0.65rem", fontFamily: "'Fraunces', Georgia, serif" }}>
                          Step {i + 1}
                        </span>
                        {thought.title && (
                          <p className="text-primary mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.9rem" }}>
                            {thought.title}
                          </p>
                        )}
                        <p className="text-foreground/60 line-clamp-3" style={{ fontSize: "0.82rem", lineHeight: "1.6" }}>
                          {thought.body}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground/50 text-center py-8" style={{ fontSize: "0.85rem" }}>
                  Loading trail...
                </p>
              )}

              {/* Continue in Thinking Space button */}
              <button
                onClick={() => {
                  const ids = thoughts.map((t) => t.id);
                  setActiveThread(ids);
                  onClose();
                  navigate("/thinking");
                }}
                className="w-full mt-6 py-3 rounded-xl bg-accent/10 text-accent hover:bg-accent/15 transition-all flex items-center justify-center gap-2"
                style={{ fontSize: "0.85rem", fontFamily: "'Fraunces', Georgia, serif" }}
              >
                <Layers size={14} />
                Continue in Thinking Space
              </button>
            </div>
    </ResponsiveSheet>
  );
}
